'use client'

import { useEffect, useState } from 'react'

import styled from 'styled-components'
import { Button, Modal, Typography } from 'shared/ui'
import { useTranslation } from 'react-i18next'
import { BoardToolbarAction } from 'shared/constants/toolbar'
import { formatDate } from 'shared/utils/time'

import { useAppDispatch, useAppSelector } from '../../../shared/config/store'
import { filesUploadingActions } from '../model/slice/filesUploadingSlice'
import { closeModal } from '../../common/model/slice/moadlSlice'
import { selectModalType } from '../../common/model/selectors/modalSelectors'

import { DuplicatePreview } from './DuplicatePreview'

export enum DuplicateActionChoice {
  Cancel = 'cancel',
  KeepBoth = 'keep_both',
  Replace = 'replace',
}

export const DuplicateActionModal = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const duplicateModal = useAppSelector((state) => state.filesUploading.duplicateModal)
  const current = duplicateModal.queue[duplicateModal.index]
  const modalType = useAppSelector(selectModalType)
  const isModalOpen = !!current && modalType === BoardToolbarAction.ResolveDuplicates
  const isGeneratingName = duplicateModal.isGeneratingName

  const duplicates = current?.duplicates ?? []
  const shouldShowDuplicatesList = duplicates.length > 1
  const [selectedDuplicateId, setSelectedDuplicateId] = useState<string | null>(null)
  useEffect(() => {
    setSelectedDuplicateId(duplicates[0]?.id ?? null)
  }, [duplicates])

  const handleClose = () => {
    dispatch(filesUploadingActions.setDuplicateNameGeneration(false))
    dispatch(closeModal())
  }

  const handleSelect = (choice: DuplicateActionChoice) => {
    if (isGeneratingName) {
      return
    }
    if (!current) {
      dispatch(closeModal())
      return
    }
    const payload: { choice: DuplicateActionChoice; target_node_id?: string } = { choice }

    if (choice === DuplicateActionChoice.Replace) {
      const fallbackId = duplicates[0]?.id
      payload.target_node_id = selectedDuplicateId || fallbackId
    }

    dispatch(filesUploadingActions.resolveDuplicateChoice(payload))
  }

  if (!current) {
    return null
  }

  return (
    <Modal
      title={t('l_library_modalDuplicateAssetTitle')}
      open={isModalOpen}
      onCancel={handleClose}
      footer={null}
      width={!isGeneratingName ? 625 : 640}
      destroyOnClose
    >
      <SContent>
        <Typography.Text>
          {t('l_library_modallDuplicateAsset', { filename: current.fileName })}
        </Typography.Text>

        {shouldShowDuplicatesList && (
          <SDuplicatesList>
            <Typography.Text type="secondary">
              {t('l_library_modalDuplicateAssetSelectTarget', { defaultValue: 'Выберите файл для замены' })}
            </Typography.Text>

            {duplicates.map(duplicate => (
              <SDuplicateItem key={duplicate.id}>
                <input
                  type="radio"
                  name={`duplicate-${current.fileKey}`}
                  value={duplicate.id}
                  checked={selectedDuplicateId === duplicate.id}
                  onChange={() => setSelectedDuplicateId(duplicate.id)}
                />

                <SDuplicateInfo>
                  <Typography.Text strong>
                    { duplicate.name }
                  </Typography.Text>

                  {duplicate.updated && (
                    <Typography.Text type="secondary">
                      {t('l_library_modalDuplicateAssetUpdated', {
                        updated: formatDate(duplicate.updated),
                        defaultValue: `Обновлено: ${formatDate(duplicate.updated)}`
                      })}
                    </Typography.Text>
                  )}
                </SDuplicateInfo>

                <DuplicatePreview duplicate={duplicate} />
              </SDuplicateItem>
            ))}
          </SDuplicatesList>
        )}

        <SButtonsContainer>
          <Button size="large"
            onClick={() => handleSelect(DuplicateActionChoice.Cancel)}
            disabled={isGeneratingName}
          >
            {t('l_library_modalDuplicateAssetCancel')}
          </Button>

          <Button size="large"
            onClick={() => handleSelect(DuplicateActionChoice.KeepBoth)}
            loading={isGeneratingName}
            disabled={isGeneratingName}
          >
            {t('l_library_modalDuplicateAssetKeepBoth')}
          </Button>

          <Button type="primary"
            size="large"
            onClick={() => handleSelect(DuplicateActionChoice.Replace)}
            disabled={!duplicates.length || isGeneratingName}
          >
            {t('l_library_modalDuplicateAssetReplace')}
          </Button>
        </SButtonsContainer>
      </SContent>
    </Modal>
  )
}

const SContent = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const SDuplicatesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 300px;
  overflow-y: auto;
`

const SDuplicateItem = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`

const SDuplicateInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
`

const SButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`
